import { useState } from "react";
import { useModal } from "../hooks/use-modal.jsx";
import useAuthenticatedAxios from "../hooks/use-authenticated-axios.js";
import Spinner from "../components/UI/Spinner.jsx";
import { currencyFormatter } from "../api/Functions.js";
import { Elements } from "@stripe/react-stripe-js";
import {
  getStripe,
  STRIPE_LOAD_ERROR_MESSAGE,
} from "../utils/constants/stripe.js";
import CheckoutForm from "../components/Stripe/CheckoutForm.jsx";
import { StripeContext } from "./stripe-context.js";
import Callout from "../components/UI/Callout";

export default function StripeProvider({ children }) {
  const [clientSecret, setClientSecret] = useState("");
  const [intentId, setIntentId] = useState("");
  const [intentStatus, setIntentStatus] = useState("");
  const [montant, setMontant] = useState("");
  const [onIntentSuccessful, setOnIntentSuccessful] = useState();
  const [intentType, setIntentType] = useState("");
  const [stripe, setStripe] = useState(null);
  const [stripeLoadError, setStripeLoadError] = useState(false);

  const options = {
    clientSecret,
    appearance: {
      theme: "flat",
    },
    paymentMethodCreation: "manual",
  };

  const [Modal, closeModal, openModal] = useModal();
  const [modalLoading, setModalLoading] = useState(false);
  const axios = useAuthenticatedAxios();

  async function createPaymentIntent(
    priceId,
    paymentMethodId,
    additional_data = {},
  ) {
    const res = await axios.post(`/stripe/payment-intent`, {
      price_id: priceId,
      payment_method_id: paymentMethodId,
      ...additional_data,
    });
    setMontant(res.data.price);
    setClientSecret(res.data.client_secret);
    setIntentId(res.data.id);
  }

  async function createSetupIntent() {
    const res = await axios.post(`/stripe/setup-intent`);
    setClientSecret(res.data.client_secret);
    setIntentId(res.data.id);
  }

  async function ensureStripe() {
    try {
      setStripeLoadError(false);
      setStripe(await getStripe());
      return true;
    } catch (e) {
      console.error(e);
      setStripeLoadError(true);
      setModalLoading(false);
      return false;
    }
  }

  async function initiatePayment(priceId, options = {}) {
    const { onPaymentSuccessful, paymentMethodId, ...additionalData } = options;
    try {
      setModalLoading(true);
      setIntentType("payment");
      openModal();
      if (!(await ensureStripe())) return;
      await createPaymentIntent(priceId, paymentMethodId, additionalData);
      setOnIntentSuccessful(() => onPaymentSuccessful);
      setModalLoading(false);
    } catch (e) {
      console.error(e);
    }
  }

  async function initiateSetup(options = {}) {
    const { onSetupSuccessful } = options;
    try {
      setModalLoading(true);
      setIntentType("setup");
      openModal();
      if (!(await ensureStripe())) return;
      await createSetupIntent();
      setOnIntentSuccessful(() => onSetupSuccessful);
      setModalLoading(false);
    } catch (e) {
      console.error(e);
    }
  }

  async function cancelIntent() {
    if (intentStatus !== "succeeded" && intentId) {
      try {
        await axios.delete(`/stripe/${intentType}-intent/${intentId}`);
      } catch (e) {
        console.error(e);
      }
    }
    setIntentId("");
    setClientSecret("");
    setIntentStatus("");
    setIntentType("");
    setStripeLoadError(false);
    closeModal();
  }

  function finishPayment() {
    setClientSecret("");
    setIntentId("");
    setIntentStatus("");
    setIntentType("");
    setStripeLoadError(false);
    closeModal();
  }

  return (
    <StripeContext.Provider
      value={{
        initiatePayment,
        initiateSetup,
        cancelIntent,
        finishPayment,
        onIntentSuccessful,
      }}
    >
      {children}
      <Modal size="medium" onCancel={cancelIntent}>
        {modalLoading && <Spinner width="3em" />}
        {stripeLoadError && (
          <Callout variant="danger">{STRIPE_LOAD_ERROR_MESSAGE}</Callout>
        )}
        {clientSecret && stripe && (
          <>
            {intentType === "payment" && (
              <p className={"primaryText marginB10"}>
                Montant de votre achat : {currencyFormatter(montant)}{" "}
              </p>
            )}
            <Elements options={options} stripe={stripe}>
              <CheckoutForm
                intentType={intentType}
                intentId={intentId}
                clientSecret={clientSecret}
                intentStatus={intentStatus}
                setIntentStatus={setIntentStatus}
                finishPayment={finishPayment}
                onIntentSuccessful={onIntentSuccessful}
              />
            </Elements>
          </>
        )}
      </Modal>
    </StripeContext.Provider>
  );
}
